import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search as SearchIcon, ArrowLeft } from "lucide-react";

/**
 * Directory — every discoverable member, strictly alphabetical. A→Z jump
 * rail on desktop; sticky letter headers on mobile. Search by name or
 * username only. Paginated with "Load more" button (never infinite scroll).
 *
 * The Add-friend button lives on cards but is a stub until phase (c) —
 * relationship state is fetched from `/commons/friend-status` (not built
 * yet), so for now every card shows a disabled "add friend — soon".
 */
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Directory() {
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState("");
  const [members, setMembers] = useState([]);
  const [nextAfter, setNextAfter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setMembers([]);
    setNextAfter(null);
    (async () => {
      try {
        const r = await api.get("/commons/directory", { params: buildParams(q, letter) });
        if (cancel) return;
        setMembers(r.data.members || []);
        setNextAfter(r.data.next_after || null);
      } catch { if (!cancel) setMembers([]); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [q, letter]);

  async function loadMore() {
    if (!nextAfter || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = buildParams(q, letter);
      params.after = nextAfter;
      const r = await api.get("/commons/directory", { params });
      setMembers(cur => [...cur, ...(r.data.members || [])]);
      setNextAfter(r.data.next_after || null);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  }

  function onSearchChange(e) {
    const v = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setQ(v); setLetter(""); }, 240);
  }

  // Which letters actually appear in the current result set — the rail
  // dims letters that would return no matches (still clickable — the server
  // will honestly return an empty page).
  const activeLetters = useMemo(() => {
    const set = new Set(members.map(m => m.letter));
    return set;
  }, [members]);

  // Group members by first letter for the sticky-header mobile layout.
  const grouped = useMemo(() => {
    const out = [];
    let currentLetter = null;
    for (const m of members) {
      if (m.letter !== currentLetter) {
        currentLetter = m.letter;
        out.push({ letter: currentLetter, items: [] });
      }
      out[out.length - 1].items.push(m);
    }
    return out;
  }, [members]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="directory">
      <Link to="/commons" data-testid="back-to-commons" className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors">
        <ArrowLeft size={12} strokeWidth={1.5} />
        back to the commons
      </Link>

      <header className="text-center mt-8">
        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          the directory
        </p>
        <h1 className="mt-3 font-serif-lit text-3xl sm:text-5xl text-[var(--cream)] leading-[1.05]">
          find people
        </h1>
        <p className="mt-3 font-serif-lit italic text-[var(--cream-dim)] text-base sm:text-lg">
          in alphabetical order, always
        </p>
      </header>

      {/* Search */}
      <div className="mt-8 room-search" data-testid="directory-search">
        <input
          type="search"
          onChange={onSearchChange}
          placeholder="search by name or username…"
          data-testid="directory-search-input"
          autoComplete="off"
        />
        <SearchIcon size={14} strokeWidth={1.5} className="room-search-icon" />
      </div>

      <div className="mt-8 flex gap-6">
        {/* Members grid */}
        <div className="flex-1 min-w-0" data-testid="directory-list">
          {loading ? (
            <p className="text-center py-12 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]" data-testid="directory-loading">
              opening the address book…
            </p>
          ) : members.length === 0 ? (
            <div className="text-center py-12" data-testid="directory-empty">
              <p className="font-serif-lit text-xl text-[var(--cream)]">no matches</p>
              <p className="mt-2 font-garamond italic text-[var(--cream-dim)]">
                try another spelling, or clear the search.
              </p>
            </div>
          ) : (
            <ul className="space-y-6">
              {grouped.map(group => (
                <li key={group.letter}>
                  <h2
                    className="sticky top-0 z-10 py-2 font-cinzel text-[10px] tracking-[0.30em] uppercase text-[var(--brass)] bg-[var(--bg)]/95 backdrop-blur-sm"
                    data-testid={`directory-letter-${group.letter}`}
                  >
                    {group.letter}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    {group.items.map(m => (
                      <MemberCard key={m.user_id} m={m} />
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {nextAfter && !loading && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="directory-load-more"
                className="lc-btn-ghost"
              >
                {loadingMore ? "opening…" : "load more"}
              </button>
            </div>
          )}
        </div>

        {/* A–Z rail (desktop only) — clicking a letter jumps a filter */}
        <nav
          className="hidden md:block shrink-0 sticky top-8 self-start"
          aria-label="jump to letter"
          data-testid="directory-az-rail"
        >
          <ul className="flex flex-col gap-0.5">
            {ALPHABET.map(L => {
              const has = activeLetters.has(L);
              const active = letter === L;
              return (
                <li key={L}>
                  <button
                    onClick={() => { setLetter(letter === L ? "" : L); setQ(""); }}
                    data-testid={`az-${L}`}
                    aria-current={active ? "true" : undefined}
                    className={`w-6 h-6 flex items-center justify-center font-mono-lc text-[10px] tracking-widest rounded transition-colors ${
                      active
                        ? "bg-[var(--brass)] text-[var(--bg)]"
                        : has
                          ? "text-[var(--cream-dim)] hover:text-[var(--brass)]"
                          : "text-[var(--cream-mute)] opacity-40 hover:opacity-75"
                    }`}
                  >
                    {L}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}

function buildParams(q, letter) {
  const p = {};
  if (q) p.q = q;
  if (letter) p.letter = letter;
  return p;
}

function MemberCard({ m }) {
  return (
    <Link
      to={`/u/${m.username}`}
      data-testid={`member-card-${m.username}`}
      className="placed-card p-4 flex items-center gap-4 hover:border-[var(--brass)]"
    >
      <div className="shrink-0">
        {m.picture ? (
          <img src={m.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-[rgba(179,137,90,0.3)]" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.3)] flex items-center justify-center font-serif-lit text-lg text-[var(--brass)]">
            {(m.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif-lit text-[var(--cream)] text-base truncate">{m.name}</p>
        <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)] truncate">@{m.username}</p>
        {m.bio && (
          <p className="mt-1 font-garamond italic text-[var(--cream-dim)] text-sm line-clamp-1">{m.bio}</p>
        )}
      </div>
    </Link>
  );
}
